import {
  aiSettingsToAiReplyConfig,
  aiSettingsToAssignAiOnCreatedConfig,
  type AiSyncInput,
} from "@redotech/effect-api-shared/default-rules/support-settings-mapping";
import {
  SUPPORT_INSIGHT_SLOT_COUNT,
  SupportInsightActionId,
} from "@redotech/redo-support-settings-common/support-insight-slots";
import { Team, teamCanUseAi } from "@redotech/redo-team-common/team";

/** Whether the team has a live Facebook page and a live Instagram account in CRDB. */
export interface SupportInsightMetaConnections {
  facebook: boolean;
  instagram: boolean;
}

export const noSupportInsightMetaConnections: SupportInsightMetaConnections = {
  facebook: false,
  instagram: false,
};

/** Platform flags use schema defaults: comment AI off, the other channels on. */
export interface SupportInsightSignals {
  aiAllowed: boolean;
  supportEnabled: boolean;
  autopilotEnabled: boolean;
  /** Email, social, SMS, Attentive, Postscript, or TikTok AI is on. */
  anyCommunicationChannelEnabled: boolean;
  emailAiEnabled: boolean;
  widgetEnabled: boolean;
  widgetAiEnabled: boolean;
  facebookConnected: boolean;
  instagramConnected: boolean;
  facebookMessagesAiEnabled: boolean;
  facebookCommentsAiEnabled: boolean;
  instagramDmAiEnabled: boolean;
  instagramCommentsAiEnabled: boolean;
  facebookCommentsIngestEnabled: boolean;
  instagramCommentsIngestEnabled: boolean;
}

function flag(
  value: boolean | null | undefined,
  whenMissing: boolean,
): boolean {
  return value ?? whenMissing;
}

/** Channels the reply rule handles. Autopilot gates every channel except chat. */
function routedAiChannels(
  ai: NonNullable<Team["settings"]["support"]>["ai"] | undefined,
): ReadonlySet<string> {
  const platforms = ai?.enabledPlatforms;
  return new Set(
    aiSettingsToAiReplyConfig({
      autopilotEnabled: ai?.autopilotEnabled === true,
      automateLiveChat: ai?.automateLiveChat === true,
      enabledPlatforms: {
        email: flag(platforms?.email, true),
        facebook: flag(platforms?.facebook, true),
        facebookComments: flag(platforms?.facebookComments, false),
        instagram: flag(platforms?.instagram, true),
        instagramComments: flag(platforms?.instagramComments, false),
        sms: flag(platforms?.sms, true),
        attentive: flag(platforms?.attentive, true),
        postscript: flag(platforms?.postscript, true),
        tiktok: flag(platforms?.tiktok, true),
      },
    }).configurations.channels,
  );
}

export function supportInsightSignalsFromTeam(
  team: Team,
  metaConnections: SupportInsightMetaConnections,
): SupportInsightSignals {
  const support = team.settings?.support;
  const ai = support?.ai;
  const routed = routedAiChannels(ai);

  return {
    aiAllowed: teamCanUseAi(team),
    supportEnabled: support?.enabled === true,
    autopilotEnabled: ai?.autopilotEnabled === true,
    anyCommunicationChannelEnabled: [...routed].some(
      (channel) => channel !== "redoChat",
    ),
    emailAiEnabled: routed.has("gmail"),
    widgetEnabled:
      team.settings?.customerWidget?.general?.enableInAppEmbed === true,
    widgetAiEnabled: routed.has("redoChat"),
    facebookConnected: metaConnections.facebook,
    instagramConnected: metaConnections.instagram,
    facebookMessagesAiEnabled: routed.has("facebook"),
    facebookCommentsAiEnabled: routed.has("facebookComments"),
    instagramDmAiEnabled: routed.has("instagram"),
    instagramCommentsAiEnabled: routed.has("instagramComments"),
    facebookCommentsIngestEnabled: flag(
      support?.facebook?.commentsEnabled,
      true,
    ),
    instagramCommentsIngestEnabled: flag(
      support?.instagram?.commentsEnabled,
      true,
    ),
  };
}

export interface SocialChannelGap {
  network: "Facebook" | "Instagram";
  channel:
    | "facebookDm"
    | "facebookComments"
    | "instagramDm"
    | "instagramComments";
}

const socialChannelRules: {
  network: SocialChannelGap["network"];
  channel: SocialChannelGap["channel"];
  open(signals: SupportInsightSignals): boolean;
}[] = [
  {
    network: "Facebook",
    channel: "facebookDm",
    open: (signals) =>
      signals.facebookConnected && !signals.facebookMessagesAiEnabled,
  },
  {
    network: "Facebook",
    channel: "facebookComments",
    open: (signals) =>
      signals.facebookConnected &&
      signals.facebookCommentsIngestEnabled &&
      !signals.facebookCommentsAiEnabled,
  },
  {
    network: "Instagram",
    channel: "instagramDm",
    open: (signals) =>
      signals.instagramConnected && !signals.instagramDmAiEnabled,
  },
  {
    network: "Instagram",
    channel: "instagramComments",
    open: (signals) =>
      signals.instagramConnected &&
      signals.instagramCommentsIngestEnabled &&
      !signals.instagramCommentsAiEnabled,
  },
];

/** Connected social channels whose AI is still off. */
export function socialChannelGaps(
  signals: SupportInsightSignals,
): SocialChannelGap[] {
  return socialChannelRules
    .filter((rule) => rule.open(signals))
    .map(({ network, channel }) => ({ network, channel }));
}

/** Settings the Enable button turns on for one insight. */
export interface SupportInsightEnablement {
  autopilot?: boolean;
  email?: boolean;
  liveChat?: boolean;
  widget?: boolean;
  facebookMessages?: boolean;
  facebookComments?: boolean;
  instagramMessages?: boolean;
  instagramComments?: boolean;
}

/** The switches behind an insight's recommended action. */
export function supportInsightEnablement(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
): SupportInsightEnablement {
  switch (id) {
    case "ai-ticket-volume":
      return {};
    case "email-channel":
      return {
        ...(signals.autopilotEnabled ? {} : { autopilot: true }),
        email: true,
      };
    case "widget-ai":
      return { liveChat: true };
    case "widget-enabled":
      return { widget: true };
    case "social-ai": {
      const enablement: SupportInsightEnablement = signals.autopilotEnabled
        ? {}
        : { autopilot: true };
      for (const gap of socialChannelGaps(signals)) {
        switch (gap.channel) {
          case "facebookDm":
            enablement.facebookMessages = true;
            break;
          case "facebookComments":
            enablement.facebookComments = true;
            break;
          case "instagramDm":
            enablement.instagramMessages = true;
            break;
          case "instagramComments":
            enablement.instagramComments = true;
            break;
        }
      }
      return enablement;
    }
  }
}

/** Platform flags the reply rule reads. Comment AI defaults off. */
export interface InsightPlatformFlags {
  email?: boolean;
  facebook?: boolean;
  facebookComments?: boolean;
  instagram?: boolean;
  instagramComments?: boolean;
  sms?: boolean;
  attentive?: boolean;
  postscript?: boolean;
  tiktok?: boolean;
}

/** AI settings already stored on the team, before this enable. */
export interface InsightStoredAiSettings {
  autopilotEnabled: boolean;
  automateLiveChat: boolean;
  platforms: InsightPlatformFlags;
}

const platformsOnWhenMissing = new Set<keyof InsightPlatformFlags>([
  "email",
  "facebook",
  "instagram",
  "sms",
  "attentive",
  "postscript",
  "tiktok",
]);

/**
 * Platforms this enable writes. Turning autopilot on closes every platform
 * the action does not name, because a missing flag defaults to on.
 */
export function insightPlatformFlags(
  change: SupportInsightEnablement,
): InsightPlatformFlags {
  if (change.autopilot) {
    return {
      email: change.email === true,
      facebook: change.facebookMessages === true,
      facebookComments: change.facebookComments === true,
      instagram: change.instagramMessages === true,
      instagramComments: change.instagramComments === true,
      sms: false,
      attentive: false,
      postscript: false,
      tiktok: false,
    };
  }
  const flags: InsightPlatformFlags = {};
  if (change.email) {
    flags.email = true;
  }
  if (change.facebookMessages) {
    flags.facebook = true;
  }
  if (change.facebookComments) {
    flags.facebookComments = true;
  }
  if (change.instagramMessages) {
    flags.instagram = true;
  }
  if (change.instagramComments) {
    flags.instagramComments = true;
  }
  return flags;
}

export function insightStoredAiSettings(team: Team): InsightStoredAiSettings {
  const ai = team.settings?.support?.ai;
  const platforms = ai?.enabledPlatforms;
  return {
    autopilotEnabled: ai?.autopilotEnabled === true,
    automateLiveChat: ai?.automateLiveChat === true,
    platforms: {
      email: platforms?.email ?? undefined,
      facebook: platforms?.facebook ?? undefined,
      facebookComments: platforms?.facebookComments ?? undefined,
      instagram: platforms?.instagram ?? undefined,
      instagramComments: platforms?.instagramComments ?? undefined,
      sms: platforms?.sms ?? undefined,
      attentive: platforms?.attentive ?? undefined,
      postscript: platforms?.postscript ?? undefined,
      tiktok: platforms?.tiktok ?? undefined,
    },
  };
}

/** Post-write AI settings, in the shape the default-rule mappers take. */
export function insightAiSyncInput(
  stored: InsightStoredAiSettings,
  change: SupportInsightEnablement,
): AiSyncInput {
  const writes = insightPlatformFlags(change);
  const platform = (key: keyof InsightPlatformFlags): boolean => {
    const written = writes[key];
    if (written !== undefined) {
      return written;
    }
    const current = stored.platforms[key];
    if (current !== undefined) {
      return current;
    }
    return platformsOnWhenMissing.has(key);
  };
  return {
    autopilotEnabled: change.autopilot === true || stored.autopilotEnabled,
    automateLiveChat: change.liveChat === true || stored.automateLiveChat,
    enabledPlatforms: {
      email: platform("email"),
      facebook: platform("facebook"),
      facebookComments: platform("facebookComments"),
      instagram: platform("instagram"),
      instagramComments: platform("instagramComments"),
      sms: platform("sms"),
      attentive: platform("attentive"),
      postscript: platform("postscript"),
      tiktok: platform("tiktok"),
    },
  };
}

function insightTouchesAiRules(change: SupportInsightEnablement): boolean {
  return (
    change.autopilot === true ||
    change.email === true ||
    change.liveChat === true ||
    change.facebookMessages === true ||
    change.facebookComments === true ||
    change.instagramMessages === true ||
    change.instagramComments === true
  );
}

/** Reply and assign-AI rule updates for an enable. Undefined for widget-only. */
export function insightRuleConfigUpdates(
  stored: InsightStoredAiSettings,
  change: SupportInsightEnablement,
) {
  if (!insightTouchesAiRules(change)) {
    return undefined;
  }
  const input = insightAiSyncInput(stored, change);
  return [
    aiSettingsToAiReplyConfig(input),
    aiSettingsToAssignAiOnCreatedConfig(input),
  ];
}

const aiUnavailable = "AI Customer Support is not available for this merchant.";

/** Why this action cannot be chosen. Undefined means the merchant qualifies. */
export function supportInsightDisabledReason(
  id: SupportInsightActionId,
  signals: SupportInsightSignals,
): string | undefined {
  switch (id) {
    case "ai-ticket-volume":
      return "Ticket percentage allocation has been retired.";
    case "email-channel":
      if (!signals.aiAllowed) {
        return aiUnavailable;
      }
      if (signals.emailAiEnabled) {
        return "Email is already on for AI.";
      }
      return undefined;
    case "widget-ai":
      if (!signals.aiAllowed) {
        return aiUnavailable;
      }
      if (!signals.widgetEnabled) {
        return "The chat widget is off.";
      }
      if (signals.widgetAiEnabled) {
        return "AI is already on for the chat widget.";
      }
      return undefined;
    case "widget-enabled":
      if (!signals.supportEnabled) {
        return "Support is off for this merchant.";
      }
      if (signals.widgetEnabled) {
        return "The chat widget is already on.";
      }
      return undefined;
    case "social-ai":
      if (!signals.aiAllowed) {
        return aiUnavailable;
      }
      if (socialChannelGaps(signals).length > 0) {
        return undefined;
      }
      if (!signals.facebookConnected && !signals.instagramConnected) {
        return "No Facebook or Instagram page is connected.";
      }
      return "AI is already on for the connected social channels.";
  }
}

/** Insights this merchant still qualifies for, in display order. */
export function qualifySupportInsights(
  signals: SupportInsightSignals,
): SupportInsightActionId[] {
  return SupportInsightActionId.filter(
    (id) => supportInsightDisabledReason(id, signals) === undefined,
  );
}

export const supportInsightLabels: Record<SupportInsightActionId, string> = {
  "ai-ticket-volume": "Retired ticket allocation",
  "email-channel": "Enabling AI on email",
  "widget-ai": "Enabling AI on chat widget",
  "widget-enabled": "Enabling chat widget",
  "social-ai": "Enabling AI on social",
};

export function defaultInsightSlots(
  qualified: readonly SupportInsightActionId[],
): (SupportInsightActionId | null)[] {
  return Array.from(
    { length: SUPPORT_INSIGHT_SLOT_COUNT },
    (_, index) => qualified[index] ?? null,
  );
}

/** Drops a saved pick that no longer qualifies. Empty slots stay empty. */
export function resolveInsightSlots(
  qualified: readonly SupportInsightActionId[],
  overrides: readonly (SupportInsightActionId | null)[] | null,
): (SupportInsightActionId | null)[] {
  if (!overrides) {
    return defaultInsightSlots(qualified);
  }
  const used = new Set<SupportInsightActionId>();
  return Array.from({ length: SUPPORT_INSIGHT_SLOT_COUNT }, (_, index) => {
    const id = overrides[index];
    if (!id || !qualified.includes(id) || used.has(id)) {
      return null;
    }
    used.add(id);
    return id;
  });
}

/** Puts `id` in `index`, swapping with the slot that already shows it. */
export function assignInsightSlot(
  slots: readonly (SupportInsightActionId | null)[],
  index: number,
  id: SupportInsightActionId,
): (SupportInsightActionId | null)[] {
  const next = Array.from(
    { length: SUPPORT_INSIGHT_SLOT_COUNT },
    (_, slot) => slots[slot] ?? null,
  );
  const other = next.findIndex((value, slot) => slot !== index && value === id);
  const previous = next[index] ?? null;
  if (other !== -1) {
    next[other] = previous;
  }
  next[index] = id;
  return next;
}
